/**
 * Zero schema — shared between zero-cache, web client, mobile client.
 * Mirrors Drizzle schema in apps/api/src/db/schema.ts.
 *
 * Naming: Zero uses snake_case column names so they match the Postgres
 * source-of-truth tables directly (zero-cache replicates raw column names).
 *
 * `any` is used inside permission expression builders because Zero's
 * generic relationships graph doesn't fully unify across nested exists()
 * callbacks in the current type-defs. Replace with stronger types when
 * the upstream API stabilises.
 */

import {
  ANYONE_CAN,
  boolean,
  createSchema,
  definePermissions,
  json,
  number,
  relationships,
  string,
  table,
  type PermissionsConfig,
} from "@rocicorp/zero";

// timestamptz columns arrive over the wire as epoch ms numbers.
const timestamp = () => number();
const uuid = () => string();

// ─── tables ────────────────────────────────────────────────────────────────
const people = table("people")
  .columns({
    id: uuid(),
    user_id: uuid().optional(),
    display_name: string(),
    given_name: string().optional(),
    family_name: string().optional(),
    handle: string().optional(),
    avatar_media_id: uuid().optional(),
    birthday: number().optional(), // DATE → epoch ms days, but Zero ships as number
    birthplace: string().optional(),
    death_date: number().optional(),
    bio: string().optional(),
    created_by: uuid().optional(),
    merged_into: uuid().optional(),
    created_at: timestamp(),
    updated_at: timestamp(),
    deleted_at: timestamp().optional(),
  })
  .primaryKey("id");

const users = table("users")
  .columns({
    id: uuid(),
    person_id: uuid(),
    email: string(),
    email_verified: boolean(),
    name: string(),
    image: string().optional(),
    preferences: json(),
    storage_bytes_used: number(),
    created_at: timestamp(),
    updated_at: timestamp(),
    deleted_at: timestamp().optional(),
  })
  .primaryKey("id");

const folders = table("folders")
  .columns({
    id: uuid(),
    owner_user_id: uuid(),
    parent_id: uuid().optional(),
    name: string(),
    position: number().optional(),
    created_at: timestamp(),
  })
  .primaryKey("id");

const collections = table("collections")
  .columns({
    id: uuid(),
    owner_user_id: uuid(),
    name: string(),
    icon: string().optional(),
    theme_color: string().optional(),
    cover_media_id: uuid().optional(),
    folder_id: uuid().optional(),
    archived_at: timestamp().optional(),
    created_at: timestamp(),
    updated_at: timestamp(),
    deleted_at: timestamp().optional(),
  })
  .primaryKey("id");

const entries = table("entries")
  .columns({
    id: uuid(),
    kind: string(),
    author_user_id: uuid(),
    title: string().optional(),
    body: string().optional(),
    occurred_at: timestamp().optional(),
    occurred_end_at: timestamp().optional(),
    place_name: string().optional(),
    payload: json(),
    created_at: timestamp(),
    updated_at: timestamp(),
    deleted_at: timestamp().optional(),
  })
  .primaryKey("id");

const media = table("media")
  .columns({
    id: uuid(),
    owner_user_id: uuid(),
    kind: string(),
    mime_type: string(),
    byte_size: number(),
    original_filename: string().optional(),
    width: number().optional(),
    height: number().optional(),
    duration_seconds: number().optional(),
    captured_at: timestamp().optional(),
    r2_key: string(),
    source: string(),
    ingest_status: string(),
    stream_id: string().optional(),
    created_at: timestamp(),
    deleted_at: timestamp().optional(),
  })
  .primaryKey("id");

const entryMedia = table("entry_media")
  .columns({
    entry_id: uuid(),
    media_id: uuid(),
    position: number(),
    created_at: timestamp(),
  })
  .primaryKey("entry_id", "media_id");

const collectionEntries = table("collection_entries")
  .columns({
    collection_id: uuid(),
    entry_id: uuid(),
    added_by: uuid(),
    added_at: timestamp(),
  })
  .primaryKey("collection_id", "entry_id");

const grants = table("grants")
  .columns({
    id: uuid(),
    subject_user_id: uuid(),
    object_type: string(),
    object_id: uuid(),
    role: string(),
    granted_via: string().optional(),
    granted_by: uuid().optional(),
    created_at: timestamp(),
  })
  .primaryKey("id");

const relationshipsTbl = table("relationships")
  .columns({
    id: uuid(),
    from_person_id: uuid(),
    to_person_id: uuid(),
    kind: string(),
    created_by: uuid(),
    created_at: timestamp(),
  })
  .primaryKey("id");

const invites = table("invites")
  .columns({
    id: uuid(),
    object_type: string(),
    object_id: uuid(),
    invited_by: uuid(),
    invited_email: string().optional(),
    invited_person_id: uuid().optional(),
    token: string().optional(),
    role: string(),
    max_uses: number().optional(),
    use_count: number(),
    expires_at: timestamp().optional(),
    rsvp_status: string().optional(),
    rsvp_updated_at: timestamp().optional(),
    accepted_at: timestamp().optional(),
    accepted_by: uuid().optional(),
    revoked_at: timestamp().optional(),
    created_at: timestamp(),
  })
  .primaryKey("id");

const interactions = table("interactions")
  .columns({
    id: uuid(),
    actor_user_id: uuid(),
    kind: string(),
    target_type: string(),
    target_id: uuid(),
    body: string().optional(),
    tagged_person_id: uuid().optional(),
    parent_id: uuid().optional(),
    created_at: timestamp(),
    updated_at: timestamp(),
    deleted_at: timestamp().optional(),
  })
  .primaryKey("id");

const subscriptions = table("subscriptions")
  .columns({
    id: uuid(),
    owner_user_id: uuid(),
    tier: string(),
    storage_limit_bytes: number(),
    polar_subscription_id: string().optional(),
    status: string(),
    read_only_until: timestamp().optional(),
    current_period_end: timestamp().optional(),
    created_at: timestamp(),
    updated_at: timestamp(),
  })
  .primaryKey("id");

const subscriptionMembers = table("subscription_members")
  .columns({
    subscription_id: uuid(),
    user_id: uuid(),
    joined_at: timestamp(),
  })
  .primaryKey("subscription_id", "user_id");

// ─── relationships ────────────────────────────────────────────────────────
const usersRel = relationships(users, ({ many, one }) => ({
  person: one({
    sourceField: ["person_id"],
    destSchema: people,
    destField: ["id"],
  }),
  ownedCollections: many({
    sourceField: ["id"],
    destSchema: collections,
    destField: ["owner_user_id"],
  }),
  grantsHeld: many({
    sourceField: ["id"],
    destSchema: grants,
    destField: ["subject_user_id"],
  }),
}));

const collectionsRel = relationships(collections, ({ many, one }) => ({
  owner: one({
    sourceField: ["owner_user_id"],
    destSchema: users,
    destField: ["id"],
  }),
  folder: one({
    sourceField: ["folder_id"],
    destSchema: folders,
    destField: ["id"],
  }),
  entries: many({
    sourceField: ["id"],
    destSchema: collectionEntries,
    destField: ["collection_id"],
  }),
}));

const entriesRel = relationships(entries, ({ many, one }) => ({
  author: one({
    sourceField: ["author_user_id"],
    destSchema: users,
    destField: ["id"],
  }),
  media: many({
    sourceField: ["id"],
    destSchema: entryMedia,
    destField: ["entry_id"],
  }),
  collections: many({
    sourceField: ["id"],
    destSchema: collectionEntries,
    destField: ["entry_id"],
  }),
}));

const mediaRel = relationships(media, ({ many, one }) => ({
  owner: one({
    sourceField: ["owner_user_id"],
    destSchema: users,
    destField: ["id"],
  }),
  entries: many({
    sourceField: ["id"],
    destSchema: entryMedia,
    destField: ["media_id"],
  }),
}));

const entryMediaRel = relationships(entryMedia, ({ one }) => ({
  entry: one({
    sourceField: ["entry_id"],
    destSchema: entries,
    destField: ["id"],
  }),
  media: one({
    sourceField: ["media_id"],
    destSchema: media,
    destField: ["id"],
  }),
}));

const collectionEntriesRel = relationships(collectionEntries, ({ one }) => ({
  collection: one({
    sourceField: ["collection_id"],
    destSchema: collections,
    destField: ["id"],
  }),
  entry: one({
    sourceField: ["entry_id"],
    destSchema: entries,
    destField: ["id"],
  }),
}));

const grantsRel = relationships(grants, ({ one }) => ({
  subject: one({
    sourceField: ["subject_user_id"],
    destSchema: users,
    destField: ["id"],
  }),
}));

const interactionsRel = relationships(interactions, ({ one }) => ({
  actor: one({
    sourceField: ["actor_user_id"],
    destSchema: users,
    destField: ["id"],
  }),
}));

export const schema = createSchema({
  enableLegacyQueries: true,
  // Custom mutators only — defined in src/mutators.ts and registered
  // both client-side (apps/web/src/lib/zero.ts) and server-side via
  // PushProcessor in apps/api/src/routes/zero-push.ts.
  enableLegacyMutators: false,
  tables: [
    people,
    users,
    folders,
    collections,
    entries,
    media,
    entryMedia,
    collectionEntries,
    grants,
    relationshipsTbl,
    invites,
    interactions,
    subscriptions,
    subscriptionMembers,
  ],
  relationships: [
    usersRel,
    collectionsRel,
    entriesRel,
    mediaRel,
    entryMediaRel,
    collectionEntriesRel,
    grantsRel,
    interactionsRel,
  ],
});

export type Schema = typeof schema;

// ─── permissions ──────────────────────────────────────────────────────────
// AuthData = JWT claims populated by Better Auth's jwt plugin.
//   sub = users.id (UUID string)
type AuthData = { sub: string };

/**
 * Phase-3 permissions: per-user owner/grant rules.
 *
 * Each rule is a function `(auth, eb) => Condition`. Empty rule arrays
 * mean "no one can do this through Zero" — that's the right answer for
 * tables managed exclusively by the API surface (users, grants, jwks,
 * subscriptions). Junction tables (entry_media, collection_entries)
 * stay ANYONE_CAN — Zero composes parent visibility automatically, so
 * a junction row whose parent the user can't see won't sync.
 *
 * Where multiple rules are listed in `select`, they're OR'd. Where
 * `preMutation`/`postMutation` are both set, both must pass for an
 * update to commit.
 */
export const permissions = definePermissions<AuthData, Schema>(schema, () => {
  // Zero's PermissionRule signature is (authData, eb) => Condition. We
  // bend the column type to `any` so isCol can be reused across tables
  // with different column unions without bespoke generics per table.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isCol = (col: string) => (auth: AuthData, eb: any) =>
    eb.cmp(col, "=", auth.sub);

  // Server-managed tables — never written from Zero clients.
  const readMineOnly = (col: string) => ({
    row: {
      select: [isCol(col)],
      insert: [],
      update: { preMutation: [], postMutation: [] },
      delete: [],
    },
  });

  // Owner-only CRUD shorthand for the simple cases (folders, media).
  const ownedBy = (col: string) => ({
    row: {
      select: [isCol(col)],
      insert: [isCol(col)],
      update: { preMutation: [isCol(col)], postMutation: [isCol(col)] },
      delete: [isCol(col)],
    },
  });

  // Junction tables: Zero only syncs rows whose parents are visible to the
  // user, so we can be permissive here without leaking.
  const parentGated = {
    row: {
      select: ANYONE_CAN,
      insert: ANYONE_CAN,
      update: { preMutation: ANYONE_CAN, postMutation: ANYONE_CAN },
      delete: ANYONE_CAN,
    },
  };

  return {
    // Identity tables ─────────────────────────────────────────────────────
    users: readMineOnly("id"),
    // people: visible to everyone for now (no PII beyond display name);
    // restrict to user.user_id == sub OR related-via-relationships once
    // the relationship traversal lands in phase 4.
    people: {
      row: {
        select: ANYONE_CAN,
        insert: [isCol("created_by")],
        update: {
          preMutation: [isCol("created_by")],
          postMutation: [isCol("created_by")],
        },
        delete: [],
      },
    },
    relationships: {
      row: {
        select: ANYONE_CAN,
        insert: [isCol("created_by")],
        update: {
          preMutation: [isCol("created_by")],
          postMutation: [isCol("created_by")],
        },
        delete: [isCol("created_by")],
      },
    },

    // Owner-only tables ───────────────────────────────────────────────────
    folders: ownedBy("owner_user_id"),
    collections: ownedBy("owner_user_id"),
    entries: ownedBy("author_user_id"),
    media: ownedBy("owner_user_id"),
    invites: ownedBy("invited_by"),
    interactions: ownedBy("actor_user_id"),

    // Junctions — parent-gated by Zero ────────────────────────────────────
    entry_media: parentGated,
    collection_entries: parentGated,

    // Server-managed ──────────────────────────────────────────────────────
    grants: readMineOnly("subject_user_id"),
    subscriptions: readMineOnly("owner_user_id"),
    subscription_members: readMineOnly("user_id"),
  } satisfies PermissionsConfig<AuthData, Schema>;
});
